import { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Download, X, Smartphone } from 'lucide-react';

const DISMISS_KEY = 'farmworker_pwa_dismissed_at';
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/i.test(ua) && !window.MSStream;
};

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
};

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;

    // Respect recent dismissal
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_COOLDOWN_MS) return;

    // Android / Chrome / Edge: capture the beforeinstallprompt event
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // iOS Safari has no beforeinstallprompt — show a hint instead
    if (isIOS()) {
      setShowIOSHint(true);
      setVisible(true);
    }

    const handleInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setVisible(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-24 md:bottom-6 left-4 right-4 md:left-auto md:right-6 md:w-80 z-50 animate-fade-in"
    >
      <div className="relative bg-white border border-[#D1D1CD] rounded-lg shadow-lg p-4">
        <button
          type="button"
          onClick={handleDismiss}
          data-testid="pwa-dismiss-btn"
          aria-label="Dismiss install prompt"
          className="absolute top-2 right-2 text-[#5C5C5C] hover:text-[#1C1C1E] p-1"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#2E4632] flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 pr-6">
            <h3
              className="text-sm font-semibold text-[#1C1C1E]"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Install FarmWorker
            </h3>
            {showIOSHint ? (
              <p className="text-xs text-[#5C5C5C] mt-1 leading-relaxed">
                Tap <span className="inline-block px-1 font-mono border rounded">↑</span> Share,
                then <span className="font-semibold">Add to Home Screen</span> for a full-screen
                app experience.
              </p>
            ) : (
              <p className="text-xs text-[#5C5C5C] mt-1 leading-relaxed">
                Add to your home screen for faster access and a native app feel.
              </p>
            )}
          </div>
        </div>

        {!showIOSHint && (
          <Button
            onClick={handleInstall}
            data-testid="pwa-install-btn"
            className="w-full h-10 mt-3 bg-[#2E4632] hover:bg-[#1F3122] text-white font-semibold"
          >
            <Download className="w-4 h-4 mr-2" />
            Install app
          </Button>
        )}
      </div>
    </div>
  );
};

export default InstallPrompt;
